/**
 * ProcessWithAI formula placeholder helpers.
 * Contract (Vigen):
 *   payload.content may contain `{CALC:<calculationId>}` when LLM found a natural position.
 *   calculated  → escaped outputText
 *   needs_input → pending chip (if marker present); no fabricated chip if absent
 *   blocked/error → remove marker
 */

const CALC_MARKER_RE = /\{CALC:([^}]+)\}/g;

/**
 * Escape text for safe insertion into an HTML document as text content.
 * @param {string} text
 * @returns {string}
 */
export function escapeHtmlText(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Build display string for a calculated result (optional ~ for estimates).
 * @param {{ outputText?: string, displayValue?: string, outputUnit?: string, isEstimated?: boolean }} calc
 * @returns {string}
 */
export function formatFormulaOutputText(calc) {
  if (!calc || typeof calc !== "object") return "";
  let text = String(calc.outputText ?? "").trim();
  if (!text) {
    const value = calc.displayValue != null ? String(calc.displayValue) : "";
    const unit = calc.outputUnit != null ? String(calc.outputUnit) : "";
    text = [value, unit].filter(Boolean).join(" ").trim();
  }
  if (!text) return "";
  if (
    calc.isEstimated === true &&
    !/^[\s]*[~∼～≈]/.test(text)
  ) {
    return `~${text}`;
  }
  return text;
}

/**
 * HTML for a needs_input pending chip (must match TipTap FormulaPendingChip parse).
 * @param {{ calculationId?: string, formulaId?: string }} calc
 * @returns {string}
 */
export function buildFormulaPendingChipHtml(calc) {
  const id = escapeHtmlText(String(calc?.calculationId ?? ""));
  const formulaId = escapeHtmlText(String(calc?.formulaId ?? "formula"));
  if (!id) return "";
  return `<span data-formula-pending="${id}" contenteditable="false">⌛ ${formulaId}</span>`;
}

/**
 * Apply ProcessWithAI calculation outcomes to report HTML markers.
 * Must run before diffHtml / setContent.
 * Does not fabricate chips when a needs_input row has no marker in HTML.
 *
 * @param {string} html
 * @param {Array<Record<string, unknown>> | null | undefined} calculations
 * @returns {{ html: string, applied: string[], unresolvedMarkers: string[], needsInput: Array, blocked: Array }}
 */
export function resolveFormulaCalcMarkers(html, calculations) {
  const source = html == null ? "" : String(html);
  const list = Array.isArray(calculations) ? calculations : [];
  const byId = new Map();
  for (const c of list) {
    const id = c?.calculationId != null ? String(c.calculationId) : "";
    if (id) byId.set(id, c);
  }

  const applied = [];
  const needsInput = [];
  const blocked = [];

  let resolved = source;
  for (const [id, c] of byId.entries()) {
    const marker = `{CALC:${id}}`;
    const status = String(c?.status || "").toLowerCase();
    let replacement = "";
    if (status === "calculated") {
      replacement = escapeHtmlText(formatFormulaOutputText(c));
      applied.push(id);
    } else if (status === "needs_input") {
      needsInput.push(c);
      replacement = buildFormulaPendingChipHtml(c);
    } else if (status === "blocked") {
      blocked.push(c);
      replacement = "";
    } else {
      // error / unknown — remove marker if present; never insert a number
      replacement = "";
    }
    if (resolved.includes(marker)) {
      resolved = resolved.split(marker).join(replacement);
    }
  }

  const unresolvedMarkers = [];
  resolved = resolved.replace(CALC_MARKER_RE, (_full, id) => {
    unresolvedMarkers.push(String(id));
    return "";
  });

  return {
    html: resolved,
    applied,
    unresolvedMarkers,
    needsInput,
    blocked,
  };
}

/**
 * Build calculationRequests payload entries from the client queue.
 * @param {Array<{ calculationId: string, formulaId: string, formulaVersion?: string, nodeId: string }>} queue
 */
export function toCalculationRequests(queue) {
  if (!Array.isArray(queue) || queue.length === 0) return [];
  return queue
    .filter((q) => q && q.calculationId && q.formulaId && q.nodeId)
    .map((q) => ({
      calculationId: String(q.calculationId),
      formulaId: String(q.formulaId),
      formulaVersion: String(q.formulaVersion || "1.0.0"),
      nodeId: String(q.nodeId),
    }));
}

/**
 * @returns {string}
 */
export function createFormulaCalculationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `calc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @returns {string}
 */
export function createFormulaNodeId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `formula-node-${crypto.randomUUID()}`;
  }
  return `formula-node-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
